import { useEffect, useState, FC } from 'react'
import { useDispatch } from 'react-redux'

import Api from 'services/Api'
import { createErrorNotification } from 'store/actions/notifications'
import { FileUpload } from 'components/Forms'
import { t } from 'translations'
import { Button, List, Spinner } from 'ui'

type Props = {
  module: string
  resourceId: string
  onChange: (files?: File) => void
  files: File[]
  disabled?: boolean
}

const FileAttachmentsLegacy: FC<Props> = ({ disabled, module: appModule, resourceId, onChange, files }) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [fileInfo, setFileInfo] = useState<{ url?: string; type: string; name: string } | null>(null)

  const removeFile = async () => {
    if (fileInfo?.url) {
      // Remove only if there is an URL (it is in S3)
      const { success } = await Api.delete(`/requests/files/${resourceId}?module=${appModule}`)
      if (!success) return dispatch(createErrorNotification())
    }
    onChange()
    setFileInfo(null)
  }

  // Watch to reset the file content when creating a new request
  useEffect(() => {
    if (!files) setFileInfo(null)
  }, [files])

  useEffect(() => {
    async function fetchAttachments() {
      setIsLoading(true)
      const { result, success } = await Api(`/requests/files/${resourceId}?module=${appModule}`)
      if (!success) dispatch(createErrorNotification())
      setFileInfo(result ?? null)
      setIsLoading(false)
    }

    if (resourceId) fetchAttachments()
  }, [appModule, resourceId, dispatch])
  // If we are fetching the file information, display a loading spinner
  if (isLoading)
    return (
      <div>
        <Spinner size="lg" /> {t('forms.fileUpload.LOADING_FILE')}
      </div>
    )

  if (!fileInfo && disabled) return null
  if (!fileInfo)
    return (
      <FileUpload
        disabled={disabled}
        onChange={(file?: File) => {
          if (!file) return
          onChange(file)
          setFileInfo(file)
        }}
      />
    )
  return (
    <List className="my-3">
      <List.Header>{t('forms.fileUpload.ATTACHMENTS')}</List.Header>
      <List.Item className="list-group-item col-12 d-flex flex-row">
        {fileInfo.type === 'application/pdf' ? (
          <span className="mr-2">
            <i className="far fa-file-pdf fa-2x" />
          </span>
        ) : (
          <span className="mr-2">
            <i className="far fa-image fa-2x" />
          </span>
        )}
        <div className="flex-fill">
          <div>
            <h6 className="d-inline">
              <strong>{fileInfo.name}</strong>
            </h6>
            {!disabled && (
              <>
                <strong> · </strong>
                <Button isLink color="error" onClick={removeFile} type="button">
                  {t('forms.REMOVE')}
                </Button>
              </>
            )}
          </div>
          {fileInfo.url ? (
            <div>
              <Button isLink color="info" onClick={() => window.open(fileInfo.url, '_blank')}>
                {t('forms.fileUpload.OPEN_FILE')}
              </Button>
            </div>
          ) : null}
        </div>
      </List.Item>
    </List>
  )
}

export default FileAttachmentsLegacy
